import React from 'react';
import { QuestActivityType, QuestStatus } from 'src/enums/quests';
import { isAdmin } from 'src/redux/auth/service';
import { getPriorityLabel } from 'src/enums/quests';
import { Rating } from 'src/types/rating';
import { WorkType as WorkTypeEnum, ReviewStatus } from 'src/enums/work';
import { IAttachment } from 'src/types/attachments';
import AttachmentItem from 'src/shared/AttachmentItem';
import AttachmentPopup from 'src/shared/AttachmentPopup';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import { getWorkTypeName } from './utils';
import {
    ActivityHeader,
    ExpandText,
    AttachmentsContainer,
    ActivityMarkdownContainer,
    DescriptionClamp,
    TaskListContainer
} from './styled';
import { getQuestCreatedText } from './utils';
import useAttachmentPopup from './useAttachmentPopup';
import StatusChangeActivity from './StatusChangeActivity';
import RatingActivity from './RatingActivity';
import WorkSolvedActivity from './WorkSolvedActivity';
import TaskTitleLink from './TaskTitleLink';

export interface ActivityUpdateProps {
    activity_type: QuestActivityType;
    user: string;
    activityId?: number;
    text?: string;
    status?: QuestStatus;
    workType?: WorkTypeEnum;
    from?: QuestStatus;
    to?: QuestStatus;
    ratings?: Rating[];
    implementationPlanRequired?: boolean;
    quickQuest?: boolean;
    taskTitle?: string;
    taskId?: string;
    tasksToCreate?: string[];
    tasksDependencies?: {
        [taskTitle: string]: (string | number)[];
    };
    netWorkTime?: string;
    grossWorkTime?: string;
    rejectionReason?: string;
    solveByReviewer?: boolean;
    seniorReviewerName?: string;
    expandedDescriptions?: { [key: number]: boolean };
    blockerDescription?: string;
    attachments?: IAttachment[];
    reviewStatus?: ReviewStatus;
    priorityFrom?: number;
    priorityTo?: number;
    onToggle?: (id: number) => void;
}

const ActivityUpdate: React.FC<ActivityUpdateProps> = ({
    activity_type,
    user,
    activityId,
    text,
    status,
    from,
    to,
    workType,
    taskTitle,
    taskId,
    tasksToCreate,
    tasksDependencies,
    netWorkTime,
    grossWorkTime,
    rejectionReason,
    solveByReviewer,
    ratings,
    implementationPlanRequired,
    quickQuest,
    expandedDescriptions,
    onToggle,
    seniorReviewerName,
    blockerDescription,
    attachments,
    reviewStatus,
    priorityFrom,
    priorityTo
}) => {
    const { popupState, getAttachmentUrl, closePopup, handleNextAttachment } =
        useAttachmentPopup(attachments);

    switch (activity_type) {
        case QuestActivityType.DESCRIPTION_UPDATED: {
            const isLong = (text?.length ?? 0) > 500;
            const isExpanded = activityId
                ? !!expandedDescriptions?.[activityId]
                : false;
            return (
                <ActivityHeader>
                    <strong>{user}</strong> updated the request description, the
                    previous description was:
                    <ActivityMarkdownContainer>
                        <DescriptionClamp expanded={isExpanded}>
                            <MarkdownEditor value={text ?? ''} readOnly />
                        </DescriptionClamp>
                        {isLong && activityId && (
                            <ExpandText onClick={() => onToggle?.(activityId)}>
                                {!isExpanded ? 'see more' : 'see less'}
                            </ExpandText>
                        )}
                    </ActivityMarkdownContainer>
                </ActivityHeader>
            );
        }

        case QuestActivityType.SOLUTION_SUBMITTED:
            return (
                <>
                    <AttachmentPopup
                        isOpen={popupState.isOpen}
                        onClose={closePopup}
                        url={popupState.url}
                        type={popupState.type}
                        name={popupState.name}
                        currentIndex={popupState.currentIndex + 1}
                        totalItems={attachments?.length || 0}
                        onNext={handleNextAttachment}
                        fileId={popupState.fileId}
                    />
                    <ActivityHeader>
                        <strong>{user}</strong> completed the request
                        {user !== 'Beehive' && grossWorkTime
                            ? ` after ${grossWorkTime} total gross work time`
                            : ''}
                        {attachments && attachments.length > 0 && (
                            <AttachmentsContainer>
                                {attachments.map((item, index) => (
                                    <AttachmentItem
                                        key={index}
                                        file={item}
                                        index={index}
                                        getAttachmentUrl={getAttachmentUrl}
                                    />
                                ))}
                            </AttachmentsContainer>
                        )}
                    </ActivityHeader>
                </>
            );

        case QuestActivityType.MODIFICATIONS_REQUESTED:
            return (
                <>
                    <AttachmentPopup
                        isOpen={popupState.isOpen}
                        onClose={closePopup}
                        url={popupState.url}
                        type={popupState.type}
                        name={popupState.name}
                        currentIndex={popupState.currentIndex + 1}
                        totalItems={attachments?.length || 0}
                        onNext={handleNextAttachment}
                        fileId={popupState.fileId}
                    />
                    <ActivityHeader>
                        <strong>{user}</strong> required modifications on the
                        request.
                        <br />
                        {user !== 'Client' && text && (
                            <>New request requirements: {text}</>
                        )}
                        {attachments && attachments.length > 0 && (
                            <AttachmentsContainer>
                                {attachments.map((item, index) => (
                                    <AttachmentItem
                                        key={index}
                                        file={item}
                                        index={index}
                                        getAttachmentUrl={getAttachmentUrl}
                                    />
                                ))}
                            </AttachmentsContainer>
                        )}
                    </ActivityHeader>
                </>
            );

        case QuestActivityType.QUEST_CREATED:
            return (
                <ActivityHeader>
                    <strong>{user}</strong>{' '}
                    {getQuestCreatedText(
                        status,
                        implementationPlanRequired,
                        quickQuest,
                        user
                    )}
                </ActivityHeader>
            );

        case QuestActivityType.STATUS_CHANGED:
            return from && to ? (
                <StatusChangeActivity from={from} to={to} user={user} />
            ) : (
                <ActivityHeader />
            );

        case QuestActivityType.RATING_SUBMITTED:
            return ratings ? (
                <RatingActivity
                    isQuestRating={true}
                    ratings={ratings}
                    user={user}
                    taskTitle={taskTitle}
                    taskId={taskId}
                    workType={workType}
                />
            ) : (
                <ActivityHeader />
            );

        case QuestActivityType.WORK_STARTED: {
            const workTypeText = workType ? getWorkTypeName(workType) : '';
            return (
                <ActivityHeader>
                    <strong>{user}</strong> started working on{' '}
                    <strong>{workTypeText}</strong> work{' '}
                    <TaskTitleLink taskId={taskId} title={taskTitle} />
                </ActivityHeader>
            );
        }

        case QuestActivityType.WORK_CANCELLED: {
            const workTypeText = workType ? getWorkTypeName(workType) : '';
            return (
                <ActivityHeader>
                    <strong>{user}</strong> cancelled his work on{' '}
                    <strong>{workTypeText}</strong> work{' '}
                    <TaskTitleLink taskId={taskId} title={taskTitle} /> after{' '}
                    {grossWorkTime ? grossWorkTime : ''}{' '}
                    {blockerDescription && (
                        <>
                            and reported a blocker.
                            <br />
                            Blocker description: {blockerDescription}
                        </>
                    )}
                </ActivityHeader>
            );
        }

        case QuestActivityType.WORK_SOLVED:
            return (
                <WorkSolvedActivity
                    user={user}
                    workType={workType}
                    taskTitle={taskTitle}
                    taskId={taskId}
                    grossWorkTime={grossWorkTime}
                    attachments={attachments}
                    reviewStatus={reviewStatus}
                    seniorReviewerName={seniorReviewerName}
                    rejectionReason={rejectionReason}
                    solveByReviewer={solveByReviewer}
                    tasksToCreate={tasksToCreate}
                    tasksDependencies={tasksDependencies}
                />
            );

        case QuestActivityType.WORK_SOLUTION_RATING:
            return ratings ? (
                <RatingActivity
                    isQuestRating={false}
                    ratings={ratings}
                    user={user}
                    taskTitle={taskTitle}
                    taskId={taskId}
                    workType={workType}
                />
            ) : (
                <ActivityHeader />
            );

        case QuestActivityType.WORK_RESERVED: {
            const workTypeText = workType ? getWorkTypeName(workType) : '';
            return (
                <ActivityHeader>
                    <strong>{workTypeText}</strong> work{' '}
                    <TaskTitleLink taskId={taskId} title={taskTitle} /> was
                    reserved to the quest breakdown solver:{' '}
                    <strong>{seniorReviewerName}</strong>
                </ActivityHeader>
            );
        }

        case QuestActivityType.NET_TIME_CALCULATED:
            return (
                <ActivityHeader>
                    Total billable net work time spent on quest:{' '}
                    <strong>{netWorkTime}</strong>
                </ActivityHeader>
            );

        case QuestActivityType.WORK_DESERTED: {
            const workTypeText = workType ? getWorkTypeName(workType) : '';
            return (
                <ActivityHeader>
                    <strong>{workTypeText}</strong> work{' '}
                    <TaskTitleLink taskId={taskId} title={taskTitle} /> was
                    deserted from <strong>{user}</strong>
                    {grossWorkTime ? ` after ${grossWorkTime}` : ''}
                </ActivityHeader>
            );
        }

        case QuestActivityType.PRIORITY_CHANGED: {
            if (!priorityFrom || !priorityTo) return <ActivityHeader />;
            const fromDisplay = isAdmin()
                ? priorityFrom.toString()
                : getPriorityLabel(priorityFrom);
            const toDisplay = isAdmin()
                ? priorityTo.toString()
                : getPriorityLabel(priorityTo);
            return (
                <ActivityHeader>
                    <strong>{user}</strong> changed the request priority from{' '}
                    <strong>{fromDisplay}</strong> to{' '}
                    <strong>{toDisplay}</strong>
                </ActivityHeader>
            );
        }
        case QuestActivityType.QUEST_BREAKDOWN_SUBMITTED: {
            const taskList = tasksToCreate?.map((task, index) => {
                const deps: number[] =
                    tasksDependencies?.[task]?.filter(
                        (d): d is number => d != null
                    ) ?? [];
                const depsText =
                    deps.length > 0
                        ? ` which depends on ${deps.map((d) => `#${d + 1}`).join(', ')}`
                        : '';
                return (
                    <div key={index}>
                        #{index + 1} <strong>{task}</strong>
                        {depsText}
                    </div>
                );
            });
            return (
                <ActivityHeader>
                    Beehive submitted a breakdown with{' '}
                    <strong>{tasksToCreate?.length ?? 0}</strong> tasks:
                    <TaskListContainer>{taskList}</TaskListContainer>
                </ActivityHeader>
            );
        }

        case QuestActivityType.IMPLEMENTATION_PLAN_SUBMITTED:
            return (
                <ActivityHeader>
                    <strong>{user}</strong> submitted an implementation plan for
                    the request
                </ActivityHeader>
            );

        case QuestActivityType.IMPLEMENTATION_PLAN_MODIFIED:
            return (
                <ActivityHeader>
                    <strong>{user}</strong> modified the implementation plan for
                    the request
                </ActivityHeader>
            );

        case QuestActivityType.IMPLEMENTATION_PLAN_APPROVED:
            return (
                <ActivityHeader>
                    <strong>{user}</strong> approved the implementation plan for
                    the request
                </ActivityHeader>
            );

        case QuestActivityType.IMPLEMENTATION_PLAN_REJECTED:
            return (
                <ActivityHeader>
                    <strong>{user}</strong> rejected the implementation plan and
                    the request was cancelled
                </ActivityHeader>
            );

        case QuestActivityType.PLANNING_OFFLOADED:
            return (
                <ActivityHeader>
                    <strong>{user}</strong> sent the planning questions to
                    Beehive for answering
                </ActivityHeader>
            );

        case QuestActivityType.BEEHIVE_EXPERT_REVIEW:
            return (
                <ActivityHeader>
                    <strong>{user}</strong> asked for a beehive expert review
                    for the request
                </ActivityHeader>
            );

        case QuestActivityType.QUEST_MERGED:
            return (
                <ActivityHeader>
                    The request was merged successfully
                </ActivityHeader>
            );

        default:
            return <ActivityHeader />;
    }
};

export default ActivityUpdate;
